class VariantModalController {
  /**
   * @param {HTMLDialogElement} modal
   */
  constructor(modal) {
    this.modal = modal;
    this.trigger = document.querySelector('[data-variant-modal-trigger]');
    this.closeButton = modal.querySelector('[data-variant-modal-close]');
    this.inputs = modal.querySelectorAll('[data-variant-modal-input]');
    this.variantPicker = document.querySelector('variant-picker');
    /** @type {Element | null} */
    this.lastFocusedElement = null;

    this.init();
  }

  init() {
    if (!this.trigger || !this.modal) return;

    this.trigger.addEventListener('click', (/** @type {Event} */ e) => {
      e.preventDefault();
      this.open();
    });
    this.closeButton?.addEventListener('click', () => this.close());

    this.modal.addEventListener('close', () => this.handleClose());
    this.modal.addEventListener('click', (/** @type {MouseEvent} */ e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });

    this.inputs.forEach((/** @type {HTMLInputElement} */ input) => {
      input.addEventListener('change', (/** @type {Event} */ e) => this.handleVariantChange(e));
    });

    document.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
      if (e.key === 'Escape' && this.modal.open) {
        this.close();
      }
    });
  }

  open() {
    this.lastFocusedElement = document.activeElement;
    this.modal.showModal();
    this.modal.removeAttribute('data-closing');

    const firstInput = /** @type {HTMLInputElement | null} */ (this.modal.querySelector('input:not(:disabled)'));
    if (firstInput) {
      firstInput.focus();
    }
  }

  close() {
    this.modal.setAttribute('data-closing', '');

    setTimeout(() => {
      this.modal.close();
    }, 300);
  }

  handleClose() {
    this.modal.removeAttribute('data-closing');

    if (this.lastFocusedElement && 'focus' in this.lastFocusedElement) {
      /** @type {HTMLElement} */ (this.lastFocusedElement).focus();
      this.lastFocusedElement = null;
    }
  }

  /**
   * @param {Event} event
   */
  handleVariantChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const fieldsetIndex = input.dataset.fieldsetIndex;
    const inputId = input.dataset.inputId;

    if (!this.variantPicker) return;

    const mainInput = /** @type {HTMLInputElement | null} */ (this.variantPicker.querySelector(
      `input[data-fieldset-index="${fieldsetIndex}"][data-input-id="${inputId}"]`
    ));

    if (mainInput && !mainInput.checked) {
      mainInput.checked = true;
      mainInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const allModalInputs = this.modal.querySelectorAll('[data-variant-modal-input]');
    allModalInputs.forEach((/** @type {HTMLInputElement} */ modalInput) => {
      const label = modalInput.closest('[role="option"]');
      if (label) {
        label.setAttribute('aria-selected', modalInput.checked ? 'true' : 'false');
      }
    });

    this.close();
  }

  destroy() {
    this.trigger?.removeEventListener('click', () => this.open());
    this.closeButton?.removeEventListener('click', () => this.close());
  }
}

function initVariantModals() {
  const modals = document.querySelectorAll('[data-variant-modal]');

  modals.forEach((/** @type {HTMLDialogElement} */ modal) => {
    if (!/** @type {any} */ (modal)._variantModalController) {
      /** @type {any} */ (modal)._variantModalController = new VariantModalController(modal);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVariantModals);
} else {
  initVariantModals();
}

if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => {
    initVariantModals();
  });

  document.addEventListener('shopify:section:unload', (/** @type {Event} */ event) => {
    if (!event.target) return;
    const target = /** @type {Element} */ (event.target);
    const modals = target.querySelectorAll('[data-variant-modal]');
    modals.forEach((/** @type {HTMLDialogElement} */ modal) => {
      const modalWithController = /** @type {any} */ (modal);
      if (modalWithController._variantModalController) {
        modalWithController._variantModalController.destroy();
        modalWithController._variantModalController = null;
      }
    });
  });
}
